import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";


const DEFAULT_DASHBOARD_FILTERS = {
    search: "",
    pm: "all",
    sponsor: "all",
    health: "all",
    status: "all",
};

function parseDashboardFiltersFromSearch(search = "") {
    const params = new URLSearchParams(search || "");
    return {
        search: params.get("q") || params.get("search") || "",
        pm: params.get("pm") || "all",
        sponsor: params.get("sponsor") || "all",
        health: params.get("health") || "all",
        status: params.get("status") || "all",
    };
}

function dashboardFiltersEqual(a = {}, b = {}) {
    return (
        String(a.search || "") === String(b.search || "") &&
        String(a.pm || "all") === String(b.pm || "all") &&
        String(a.sponsor || "all") === String(b.sponsor || "all") &&
        String(a.health || "all") === String(b.health || "all") &&
        String(a.status || "all") === String(b.status || "all")
    );
}

function applyDashboardFiltersToSearch(currentSearch = "", filters = DEFAULT_DASHBOARD_FILTERS) {
    const params = new URLSearchParams(currentSearch || "");
    const normalized = { ...DEFAULT_DASHBOARD_FILTERS, ...(filters || {}) };
    const setOrDelete = (key, value, defaultValue = "all") => {
        const cleanValue = String(value || "").trim();
        if (!cleanValue || cleanValue === defaultValue) params.delete(key);
        else params.set(key, cleanValue);
    };

    setOrDelete("q", normalized.search, "");
    params.delete("search");
    setOrDelete("pm", normalized.pm);
    setOrDelete("sponsor", normalized.sponsor);
    setOrDelete("health", normalized.health);
    setOrDelete("status", normalized.status);

    return params.toString();
}

const toneMap = {
    neutral: { bg: "#FFFFFF", border: "#E2E8F0", value: "#0F172A", soft: "#F8FAFC", accent: "#64748B", glow: "rgba(100,116,139,.14)" },
    danger: { bg: "#FFF1F2", border: "#FECDD3", value: "#BE123C", soft: "#FFE4E6", accent: "#E11D48", glow: "rgba(225,29,72,.16)" },
    warning: { bg: "#FFFBEB", border: "#FDE68A", value: "#B45309", soft: "#FEF3C7", accent: "#F59E0B", glow: "rgba(245,158,11,.18)" },
    success: { bg: "#ECFDF5", border: "#A7F3D0", value: "#047857", soft: "#D1FAE5", accent: "#10B981", glow: "rgba(16,185,129,.16)" },
    info: { bg: "#EFF6FF", border: "#BFDBFE", value: "#1D4ED8", soft: "#DBEAFE", accent: "#2563EB", glow: "rgba(37,99,235,.16)" },
    purple: { bg: "#F5F3FF", border: "#DDD6FE", value: "#6D28D9", soft: "#EDE9FE", accent: "#7C3AED", glow: "rgba(124,58,237,.16)" },
};

function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min = 0, max = 100) {
    return Math.max(min, Math.min(max, safeNumber(value)));
}

function formatNumber(value, decimals = 0) {
    return safeNumber(value).toLocaleString("es-MX", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function formatMoney(value, currency = "MXN") {
    return safeNumber(value).toLocaleString("es-MX", {
        style: "currency",
        currency: currency || "MXN",
        maximumFractionDigits: 0,
    });
}

function formatDate(value) {
    if (!value) return "Sin fecha";
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

function cleanText(value) {
    return String(value || "")
        .replaceAll("Configuracion", "Configuración")
        .replaceAll("configuracion", "configuración")
        .replaceAll("Correcion", "Corrección")
        .replaceAll("correcion", "corrección")
        .replaceAll("Ejecucion", "Ejecución")
        .replaceAll("ejecucion", "ejecución")
        .replaceAll("Validacion", "Validación")
        .replaceAll("validacion", "validación")
        .replaceAll("Revision", "Revisión")
        .replaceAll("revision", "revisión")
        .replaceAll("Dictamen", "Dictamen")
        .replaceAll("actualizacion", "actualización")
        .replaceAll("Actualizacion", "Actualización")
        .replaceAll("progress", "avance")
        .replaceAll("Progress", "Avance");
}

function label(value) {
    const raw = String(value || "sin dato").toLowerCase().replaceAll("_", " ").replaceAll("-", " ").trim();
    const map = {
        critical: "Crítica",
        high: "Alta",
        medium: "Media",
        low: "Baja",
        danger: "Crítica",
        warning: "Atención",
        success: "Estable",
        info: "Informativa",
        overdue: "Vencimiento",
        vencimiento: "Vencimiento",
        seguimiento: "Seguimiento",
        blocked: "Bloqueo",
        evidence: "Evidencia",
        geo: "Geoevidencia",
        review: "Revisión",
        governance: "Gobierno",
        planning: "Planeación",
        inconsistent: "Inconsistencia",
        pendiente: "Pendiente",
        en_proceso: "En proceso",
        terminada: "Terminada",
        cancelada: "Cancelada",
        borrador: "Borrador",
        aprobado: "Aprobado",
        rechazado: "Rechazado",
        en_revision: "En revisión",
        pendiente_revision: "Pendiente revisión",
    };
    const text = map[raw] || cleanText(raw);
    return text.charAt(0).toUpperCase() + text.slice(1);
}

function scorePmoTooltip() {
    return "Score PMO: lectura ejecutiva calculada con salud del proyecto, vencimientos, tareas abiertas, evidencias, riesgos, bloqueos, entregables y presión operativa. Úsalo para priorizar atención, no como juicio personal.";
}

function financeExecutionText(finance = {}) {
    const planned = safeNumber(finance.budget_planned);
    const actual = safeNumber(finance.budget_actual);
    const pending = safeNumber(finance.pending_expenses);
    const pct = planned > 0 ? (actual / planned) * 100 : 0;
    if (planned <= 0) return "Sin presupuesto planeado capturado para interpretar ejecución financiera.";
    const pendingText = pending > 0 ? `Hay ${formatMoney(pending)} en gastos pendientes.` : "No hay gastos pendientes registrados.";
    return `Ejecución financiera: ${formatNumber(pct, 1)}% del presupuesto planeado ejercido. ${pendingText}`;
}

function toneFromHealth(value) {
    const v = String(value || "").toLowerCase();
    if (v === "rojo") return "danger";
    if (v === "amarillo") return "warning";
    if (v === "verde") return "success";
    return "neutral";
}

function toneFromSeverity(value) {
    const v = String(value || "").toLowerCase();
    if (["critical", "critica", "crítica"].includes(v)) return "danger";
    if (["high", "alta"].includes(v)) return "warning";
    if (["medium", "media"].includes(v)) return "info";
    return "neutral";
}

function toneFromExecutive(value) {
    const v = String(value || "").toLowerCase();
    if (["danger", "critical", "rojo"].includes(v)) return "danger";
    if (["warning", "amarillo"].includes(v)) return "warning";
    if (["success", "verde"].includes(v)) return "success";
    if (["purple"].includes(v)) return "purple";
    if (["info"].includes(v)) return "info";
    return "neutral";
}

function StatusPill({ children, tone = "neutral", title }) {
    const s = toneMap[tone] || toneMap.neutral;
    return (
        <span
            title={title}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                background: s.bg,
                color: s.value,
                border: `1px solid ${s.border}`,
                fontSize: 11,
                fontWeight: 950,
                whiteSpace: "nowrap",
                cursor: title ? "help" : "default",
            }}
        >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: s.accent }} />
            {children}
        </span>
    );
}

function EmptyState({ text = "Sin información disponible para esta sección." }) {
    return (
        <div style={{ border: "1px dashed #CBD5E1", background: "#F8FAFC", borderRadius: 18, padding: 18, color: "#64748B", fontSize: 13, fontWeight: 750, textAlign: "center" }}>
            {text}
        </div>
    );
}

function Block({ title, subtitle, children, action, compact = false }) {
    return (
        <section style={{ background: "rgba(255,255,255,.92)", border: "1px solid #E2E8F0", borderRadius: 24, padding: compact ? 16 : 20, boxShadow: "0 18px 45px rgba(15,23,42,.06)", minHeight: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginBottom: 16, alignItems: "flex-start" }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 950, color: "#0F172A", letterSpacing: "-.02em" }}>{title}</h2>
                    {subtitle ? <div style={{ marginTop: 4, fontSize: 12, color: "#64748B", fontWeight: 700, lineHeight: 1.4 }}>{subtitle}</div> : null}
                </div>
                {action ? <div>{action}</div> : null}
            </div>
            {children}
        </section>
    );
}

function KpiCard({ title, value, subtitle, tone = "neutral", icon = "◈", onClick, trend }) {
    const s = toneMap[tone] || toneMap.neutral;
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                background: `linear-gradient(180deg, ${s.bg} 0%, #FFFFFF 100%)`,
                border: `1px solid ${s.border}`,
                borderRadius: 22,
                padding: 18,
                width: "100%",
                textAlign: "left",
                cursor: onClick ? "pointer" : "default",
                transition: "transform .15s ease, box-shadow .15s ease",
                boxShadow: `0 16px 36px ${s.glow}`,
                position: "relative",
                overflow: "hidden",
                minHeight: 136,
            }}
        >
            <div style={{ position: "absolute", right: -28, top: -32, width: 96, height: 96, borderRadius: 999, background: s.soft, opacity: .78 }} />
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, position: "relative" }}>
                <div>
                    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 10, fontWeight: 950, letterSpacing: ".04em", textTransform: "uppercase" }}>{title}</div>
                    <div style={{ fontSize: 32, lineHeight: 1, fontWeight: 950, color: s.value, letterSpacing: "-.04em" }}>{value}</div>
                </div>
                <div style={{ width: 42, height: 42, borderRadius: 16, background: s.soft, color: s.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 950, border: `1px solid ${s.border}` }}>{icon}</div>
            </div>
            <div style={{ position: "relative", marginTop: 14 }}>
                {subtitle ? <div style={{ fontSize: 12, color: "#64748B", fontWeight: 700, lineHeight: 1.35 }}>{subtitle}</div> : null}
                {trend ? <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, padding: "6px 9px", borderRadius: 999, background: "#F8FAFC", border: "1px solid #E2E8F0", color: "#475569", fontSize: 11, fontWeight: 900 }}>{trend}</div> : null}
            </div>
        </button>
    );
}

function FiveSecondCard({ label, title, subtitle, tone = "neutral", icon = "◈", onClick }) {
    const s = toneMap[tone] || toneMap.neutral;
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                width: "100%",
                textAlign: "left",
                border: `1px solid ${s.border}`,
                borderLeft: `5px solid ${s.accent}`,
                background: "rgba(255,255,255,.92)",
                borderRadius: 22,
                padding: 18,
                boxShadow: `0 18px 38px ${s.glow}`,
                cursor: onClick ? "pointer" : "default",
                minHeight: 148,
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
                    <div style={{ marginTop: 9, color: "#0F172A", fontSize: 18, fontWeight: 950, letterSpacing: "-.03em", lineHeight: 1.16 }}>{title}</div>
                </div>
                <div style={{ width: 40, height: 40, borderRadius: 15, background: s.soft, color: s.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 950, border: `1px solid ${s.border}`, flexShrink: 0 }}>{icon}</div>
            </div>
            {subtitle ? <div style={{ marginTop: 12, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>{subtitle}</div> : null}
        </button>
    );
}

function ProgressBar({ value, color = "#2563EB", label, rightLabel }) {
    const v = clamp(value);
    return (
        <div style={{ marginBottom: 12 }}>
            {label || rightLabel ? (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 7 }}>
                    <span style={{ color: "#334155", fontSize: 12, fontWeight: 850 }}>{label}</span>
                    <span style={{ color: "#64748B", fontSize: 12, fontWeight: 900 }}>{rightLabel || `${v.toFixed(0)}%`}</span>
                </div>
            ) : null}
            <div style={{ height: 10, borderRadius: 999, background: "#E2E8F0", overflow: "hidden", border: "1px solid #E2E8F0" }}>
                <div style={{ width: `${v}%`, height: "100%", borderRadius: 999, background: color, boxShadow: `0 8px 18px ${color}40` }} />
            </div>
        </div>
    );
}

function HealthScoreMeter({ score = 0, tone = "neutral", summary = {} }) {
    const s = toneMap[toneFromExecutive(tone)] || toneMap.neutral;
    const health = clamp(score);
    return (
        <div style={{ borderRadius: 28, padding: 24, background: "linear-gradient(135deg, #0F172A 0%, #1E293B 56%, #111827 100%)", color: "#FFFFFF", position: "relative", overflow: "hidden", minHeight: 330, boxShadow: "0 28px 65px rgba(15,23,42,.25)" }}>
            <div style={{ position: "absolute", width: 260, height: 260, borderRadius: 999, right: -80, top: -90, background: "rgba(37,99,235,.28)" }} />
            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", gap: 18 }}>
                <div>
                    <div style={{ color: "#93C5FD", fontSize: 12, fontWeight: 950, letterSpacing: ".08em", textTransform: "uppercase" }}>Salud PMO</div>
                    <h2 style={{ margin: "8px 0 0", fontSize: 27, fontWeight: 950, letterSpacing: "-.04em", lineHeight: 1.08 }}>Lectura ejecutiva del portafolio</h2>
                    <p style={{ margin: "10px 0 0", color: "#CBD5E1", fontSize: 13, lineHeight: 1.55, maxWidth: 520, fontWeight: 650 }}>
                        El Score PMO combina proyectos en rojo, tareas abiertas, vencimientos, revisión, bloqueos y señales de trazabilidad.
                    </p>
                </div>
                <StatusPill tone={toneFromExecutive(tone)}>{health.toFixed(0)} / 100</StatusPill>
            </div>
            <div style={{ position: "relative", marginTop: 24, display: "grid", gridTemplateColumns: "180px 1fr", gap: 26, alignItems: "center" }}>
                <div style={{ width: 170, height: 170, borderRadius: "50%", background: `conic-gradient(${s.accent} ${health * 3.6}deg, rgba(255,255,255,.13) 0deg)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 18px 40px ${s.accent}22` }}>
                    <div style={{ width: 126, height: 126, borderRadius: "50%", background: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", border: "1px solid rgba(255,255,255,.12)" }}>
                        <div style={{ fontSize: 36, fontWeight: 950, letterSpacing: "-.05em" }}>{health.toFixed(0)}%</div>
                        <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 850 }}>salud</div>
                    </div>
                </div>
                <div>
                    <ProgressBar value={summary?.avg_progress || 0} color="#60A5FA" label="Avance promedio" rightLabel={`${formatNumber(summary?.avg_progress || 0, 0)}%`} />
                    <ProgressBar value={summary?.completed_tasks && summary?.total_tasks ? (safeNumber(summary.completed_tasks) / Math.max(1, safeNumber(summary.total_tasks))) * 100 : 0} color="#10B981" label="Cierre de tareas" rightLabel={`${formatNumber(summary?.completed_tasks || 0)}/${formatNumber(summary?.total_tasks || 0)}`} />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18 }}>
                        <MiniDarkMetric label="Rojas" value={summary?.red_projects || 0} />
                        <MiniDarkMetric label="Vencidas" value={summary?.overdue_tasks || 0} />
                        <MiniDarkMetric label="Abiertas" value={summary?.open_tasks || 0} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function MiniDarkMetric({ label, value }) {
    return (
        <div style={{ padding: 12, borderRadius: 16, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.11)" }}>
            <div style={{ color: "#94A3B8", fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}>{label}</div>
            <div style={{ marginTop: 4, color: "#FFFFFF", fontSize: 18, fontWeight: 950 }}>{formatNumber(value)}</div>
        </div>
    );
}

function ExecutiveAttentionCard({ item, onOpenTask }) {
    const tone = toneFromSeverity(item?.severity);
    const s = toneMap[tone] || toneMap.neutral;
    return (
        <button type="button" onClick={() => onOpenTask?.(item?.task_id, item?.project_id)} style={{ width: "100%", textAlign: "left", border: `1px solid ${s.border}`, background: "#FFFFFF", borderRadius: 20, padding: 15, cursor: "pointer", boxShadow: `0 12px 28px ${s.glow}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 8 }}>
                        <StatusPill tone={tone} title="Severidad de la acción sugerida por el Centro de Decisiones.">{label(item?.severity || "seguimiento")}</StatusPill>
                        <StatusPill tone="info" title="Categoría de la señal detectada: vencimiento, bloqueo, evidencia, revisión o seguimiento.">{label(item?.category)}</StatusPill>
                    </div>
                    <div style={{ color: "#0F172A", fontSize: 15, fontWeight: 950, lineHeight: 1.25 }}>{item?.wbs_code ? `${item.wbs_code} · ` : ""}{cleanText(item?.task_name || "Tarea sin nombre")}</div>
                    <div style={{ marginTop: 5, color: "#64748B", fontSize: 11, fontWeight: 800 }}>{item?.project_code || "PRJ"} · {item?.project_name || "Sin proyecto"}</div>
                </div>
                <div style={{ minWidth: 58, textAlign: "center", borderRadius: 16, background: s.bg, border: `1px solid ${s.border}`, color: s.value, padding: "8px 9px" }}>
                    <div style={{ fontSize: 21, fontWeight: 950, letterSpacing: "-.04em", lineHeight: 1 }}>{formatNumber(item?.decision_score || 0)}</div>
                    <div title={scorePmoTooltip()} style={{ marginTop: 4, fontSize: 9, fontWeight: 950, textTransform: "uppercase", cursor: "help" }}>Score PMO</div>
                </div>
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 7, flexWrap: "wrap" }}>
                {(item?.flags || []).length ? (item.flags || []).map((flag, idx) => <StatusPill key={`${flag}-${idx}`} tone={String(flag).includes("venc") ? "danger" : "warning"}>{flag}</StatusPill>) : <StatusPill tone="neutral">Seguimiento PMO</StatusPill>}
            </div>
            <div style={{ marginTop: 12, color: "#334155", fontSize: 12, fontWeight: 800, lineHeight: 1.45, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 16, padding: 12 }}>
                {cleanText(item?.recommended_action || "Revisar tarea y actualizar estado operativo.")}
            </div>
        </button>
    );
}

function ProjectHealthGraphic({ rows = [], onOpenProject, mode = "progress" }) {
    const isScore = mode === "score";

    const data = useMemo(() => {
        return [...rows]
            .map((row) => ({
                ...row,
                chart_score: clamp(row.executive_score || projectPressureScore(row)),
                progress_value: clamp(row.progress_percent),
            }))
            .sort((a, b) => isScore
                ? b.chart_score - a.chart_score
                : b.progress_value - a.progress_value || b.chart_score - a.chart_score);
    }, [rows, isScore]);

    const avgProgress = data.reduce((a, b) => a + safeNumber(b.progress_value), 0) / Math.max(1, data.length);
    const avgScore = data.reduce((a, b) => a + safeNumber(b.chart_score), 0) / Math.max(1, data.length);
    const redCount = data.filter((row) => String(row.health_status || "").toLowerCase() === "rojo").length;
    const maxValue = isScore ? Math.max(1, ...data.map((row) => safeNumber(row.chart_score))) : 100;
    const chartTitle = isScore ? "Score PMO por proyecto" : "Comparativa general de avance";
    const chartSubtitle = isScore
        ? "Lectura separada de presión/riesgo PMO. Aquí el avance no se mezcla con el score."
        : "Todos los proyectos visibles en una sola gráfica. Una barra por proyecto para leer avance en segundos.";

    if (!data.length) return <EmptyState text="No hay proyectos para visualizar en esta gráfica. Ajusta filtros o limpia la vista para recuperar la lectura comparativa." />;

    return (
        <div style={{ display: "grid", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12 }}>
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 18, padding: 14, background: "#F8FAFC" }}>
                    <div style={{ fontSize: 10, color: "#64748B", fontWeight: 950, textTransform: "uppercase" }}>{isScore ? "Mayor presión" : "Avance promedio"}</div>
                    <div style={{ marginTop: 8, fontSize: 22, color: "#0F172A", fontWeight: 950 }}>
                        {isScore ? formatNumber(data[0]?.chart_score || 0, 0) : `${formatNumber(avgProgress, 0)}%`}
                    </div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>
                        {isScore ? cleanText(data[0]?.project_name || "Sin proyecto") : "Promedio de avance de los proyectos visibles."}
                    </div>
                </div>
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 18, padding: 14, background: "#F8FAFC" }}>
                    <div style={{ fontSize: 10, color: "#64748B", fontWeight: 950, textTransform: "uppercase" }}>{isScore ? "Score PMO promedio" : "Proyectos visibles"}</div>
                    <div style={{ marginTop: 8, fontSize: 22, color: "#0F172A", fontWeight: 950 }}>{isScore ? formatNumber(avgScore, 0) : formatNumber(data.length)}</div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>{isScore ? "Presión promedio dentro de la muestra." : "Todos se muestran en la gráfica comparativa."}</div>
                </div>
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 18, padding: 14, background: "#F8FAFC" }}>
                    <div style={{ fontSize: 10, color: "#64748B", fontWeight: 950, textTransform: "uppercase" }}>Proyectos en rojo</div>
                    <div style={{ marginTop: 8, fontSize: 22, color: redCount > 0 ? "#BE123C" : "#047857", fontWeight: 950 }}>{formatNumber(redCount)}</div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>Semáforo ejecutivo de los proyectos visibles.</div>
                </div>
            </div>

            <div style={{ border: "1px solid #E2E8F0", borderRadius: 22, padding: 16, background: "#FFFFFF", boxShadow: "0 12px 28px rgba(15,23,42,.04)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ color: "#0F172A", fontSize: 14, fontWeight: 950 }}>{chartTitle}</div>
                        <div style={{ marginTop: 3, color: "#64748B", fontSize: 12, fontWeight: 750 }}>{chartSubtitle}</div>
                    </div>
                    <div style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <span title={isScore ? scorePmoTooltip() : columnTooltips.avance} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: "#64748B", fontSize: 11, fontWeight: 900, cursor: "help" }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: isScore ? "#F59E0B" : "#2563EB" }} />
                            {isScore ? "Score PMO" : "Avance"}
                        </span>
                        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", color: "#64748B", fontSize: 11, fontWeight: 900 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 999, background: "#BE123C" }} />
                            Salud roja
                        </span>
                    </div>
                </div>

                <div style={{ overflowX: "auto", padding: "0 0 8px 44px" }}>
                    <div style={{ minWidth: Math.max(760, data.length * 96), position: "relative", padding: "12px 12px 0" }}>
                        <div style={{ height: 320, position: "relative", borderLeft: "1px solid #CBD5E1", borderBottom: "1px solid #CBD5E1", background: "linear-gradient(180deg, rgba(148,163,184,.12) 1px, transparent 1px)", backgroundSize: "100% 64px" }}>
                            {[100, 75, 50, 25, 0].map((tick) => (
                                <div key={tick} style={{ position: "absolute", left: 0, right: 0, bottom: `${tick}%`, borderTop: tick === 0 ? "none" : "1px solid rgba(148,163,184,.28)" }}>
                                    <span style={{ position: "absolute", left: -38, top: -8, color: "#64748B", fontSize: 10, fontWeight: 850 }}>{tick}</span>
                                </div>
                            ))}

                            <div style={{ position: "absolute", inset: "0 12px 0 18px", display: "grid", gridTemplateColumns: `repeat(${data.length}, minmax(70px, 1fr))`, gap: 16, alignItems: "end" }}>
                                {data.map((row, idx) => {
                                    const rawValue = isScore ? safeNumber(row.chart_score) : safeNumber(row.progress_value);
                                    const displayValue = isScore ? rawValue : clamp(rawValue);
                                    const height = Math.max(4, isScore ? (displayValue / maxValue) * 100 : displayValue);
                                    const isRed = String(row.health_status || "").toLowerCase() === "rojo";
                                    const labelInside = height >= 92;
                                    return (
                                        <button
                                            key={row.project_id || idx}
                                            type="button"
                                            onClick={() => onOpenProject?.(row.project_id)}
                                            title={`Abrir ficha 360 de ${cleanText(row.project_name)}.`}
                                            style={{
                                                height: "100%",
                                                border: "none",
                                                background: "transparent",
                                                padding: 0,
                                                cursor: "pointer",
                                                display: "grid",
                                                gridTemplateRows: "1fr auto",
                                                alignItems: "end",
                                            }}
                                        >
                                            <div style={{ height: "100%", display: "flex", alignItems: "end", justifyContent: "center", paddingTop: 18 }}>
                                                <div style={{
                                                    width: 54,
                                                    height: `${height}%`,
                                                    minHeight: 12,
                                                    borderRadius: "16px 16px 4px 4px",
                                                    background: isScore ? "linear-gradient(180deg, #F59E0B, #FB7185)" : "linear-gradient(180deg, #2563EB, #38BDF8)",
                                                    boxShadow: isScore ? "0 12px 24px rgba(245,158,11,.24)" : "0 12px 24px rgba(37,99,235,.22)",
                                                    position: "relative",
                                                    outline: isRed ? "2px solid rgba(225,29,72,.28)" : "none",
                                                    outlineOffset: 3,
                                                }}>
                                                    <span style={{
                                                        position: "absolute",
                                                        top: labelInside ? 8 : -22,
                                                        left: "50%",
                                                        transform: "translateX(-50%)",
                                                        color: labelInside ? "#FFFFFF" : "#0F172A",
                                                        fontSize: 11,
                                                        fontWeight: 950,
                                                        whiteSpace: "nowrap",
                                                        textShadow: labelInside ? "0 1px 4px rgba(15,23,42,.35)" : "none",
                                                    }}>
                                                        {isScore ? `${formatNumber(displayValue, 0)}` : `${formatNumber(displayValue, 0)}%`}
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ marginTop: 10, display: "grid", gap: 6, justifyItems: "center" }}>
                                                <div style={{ color: "#0F172A", fontSize: 11, fontWeight: 950, lineHeight: 1.1, minHeight: 28, maxWidth: 94, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{cleanText(row.project_name)}</div>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 8px", borderRadius: 999, border: `1px solid ${(toneMap[toneFromHealth(row.health_status)] || toneMap.neutral).border}`, background: (toneMap[toneFromHealth(row.health_status)] || toneMap.neutral).bg, color: (toneMap[toneFromHealth(row.health_status)] || toneMap.neutral).value, fontSize: 10, fontWeight: 950 }}>
                                                    <span style={{ width: 6, height: 6, borderRadius: 999, background: (toneMap[toneFromHealth(row.health_status)] || toneMap.neutral).accent }} />
                                                    {label(row.health_status)}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProjectHealthSection({ rows = [], onOpenProject, onOpenProjects }) {
    const [view, setView] = useState("compare");
    const iconButton = (active) => ({
        height: 36,
        minWidth: 38,
        padding: "0 12px",
        borderRadius: 12,
        border: active ? "1px solid #0F172A" : "1px solid #CBD5E1",
        background: active ? "#0F172A" : "#FFFFFF",
        color: active ? "#FFFFFF" : "#334155",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 950,
        boxShadow: active ? "0 10px 24px rgba(15,23,42,.12)" : "none",
    });

    return (
        <Block
            title="Salud por proyecto"
            subtitle="Portafolio ejecutivo con lectura de detalle, avance comparativo y Score PMO por proyecto."
            action={
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <button type="button" title="Ver avance general de todos los proyectos en gráfica." onClick={() => setView("compare")} style={iconButton(view === "compare")}>▥ <span>Comparativa</span></button>
                    <button type="button" title="Ver Score PMO por proyecto." onClick={() => setView("score")} style={iconButton(view === "score")}>📊 <span>Score</span></button>
                    <button type="button" title="Ver detalle en grid ejecutivo." onClick={() => setView("detail")} style={iconButton(view === "detail")}>▤ <span>Grid</span></button>
                </div>
            }
        >
            {view === "detail" ? (
                <PortfolioTable rows={rows} onOpenProject={onOpenProject} />
            ) : (
                <ProjectHealthGraphic rows={rows} onOpenProject={onOpenProject} mode={view === "score" ? "score" : "progress"} />
            )}
        </Block>
    );
}

function normalizeTextForSearch(value) {
    return cleanText(value).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function uniqueOptions(values = []) {
    return Array.from(new Set(values.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), "es"));
}

function buildSummaryFromPortfolio(rows = []) {
    const totalProjects = rows.length;
    const activeProjects = rows.filter((p) => String(p.status || "").toLowerCase() === "activo").length;
    const completedProjects = rows.filter((p) => String(p.status || "").toLowerCase() === "terminado").length;
    const redProjects = rows.filter((p) => String(p.health_status || "").toLowerCase() === "rojo").length;
    const yellowProjects = rows.filter((p) => String(p.health_status || "").toLowerCase() === "amarillo").length;
    const greenProjects = rows.filter((p) => String(p.health_status || "").toLowerCase() === "verde").length;
    const totalTasks = rows.reduce((acc, p) => acc + safeNumber(p.total_tasks), 0);
    const openTasks = rows.reduce((acc, p) => acc + safeNumber(p.open_tasks), 0);
    const completedTasks = Math.max(0, totalTasks - openTasks);
    const overdueTasks = rows.reduce((acc, p) => acc + safeNumber(p.overdue_tasks), 0);
    const blockedTasks = rows.reduce((acc, p) => acc + safeNumber(p.blocked_tasks), 0);
    const reviewTasks = rows.reduce((acc, p) => acc + safeNumber(p.review_tasks), 0);
    const correctionTasks = rows.reduce((acc, p) => acc + safeNumber(p.correction_tasks), 0);
    const avgProgress = totalProjects ? rows.reduce((acc, p) => acc + safeNumber(p.progress_percent), 0) / totalProjects : 0;
    const riskPenalty = Math.min(100, redProjects * 14 + yellowProjects * 6 + overdueTasks * 2.4 + blockedTasks * 3.2 + reviewTasks * 1.4 + correctionTasks * 2);
    const healthScore = clamp(100 - riskPenalty);

    return {
        total_projects: totalProjects,
        active_projects: activeProjects,
        completed_projects: completedProjects,
        green_projects: greenProjects,
        yellow_projects: yellowProjects,
        red_projects: redProjects,
        avg_progress: avgProgress,
        total_tasks: totalTasks,
        open_tasks: openTasks,
        completed_tasks: completedTasks,
        blocked_tasks: blockedTasks,
        overdue_tasks: overdueTasks,
        review_tasks: reviewTasks,
        correction_tasks: correctionTasks,
        total_budget_planned: rows.reduce((acc, p) => acc + safeNumber(p.budget_planned), 0),
        total_budget_actual: rows.reduce((acc, p) => acc + safeNumber(p.budget_actual), 0),
        total_contract_value: rows.reduce((acc, p) => acc + safeNumber(p.contract_value), 0),
        health_score: healthScore,
        health_tone: healthScore < 55 ? "danger" : healthScore < 78 ? "warning" : "success",
    };
}

function buildProjectManagersFromPortfolio(rows = []) {
    const map = new Map();
    rows.forEach((project) => {
        const key = project.project_manager_name || "Sin PM";
        const current = map.get(key) || {
            user_id: project.project_manager_user_id || null,
            name: key,
            projects_count: 0,
            red_projects: 0,
            yellow_projects: 0,
            overdue_tasks: 0,
            blocked_tasks: 0,
            progress_sum: 0,
            avg_progress: 0,
        };
        current.projects_count += 1;
        current.red_projects += String(project.health_status || "").toLowerCase() === "rojo" ? 1 : 0;
        current.yellow_projects += String(project.health_status || "").toLowerCase() === "amarillo" ? 1 : 0;
        current.overdue_tasks += safeNumber(project.overdue_tasks);
        current.blocked_tasks += safeNumber(project.blocked_tasks);
        current.progress_sum += safeNumber(project.progress_percent);
        current.avg_progress = current.projects_count ? current.progress_sum / current.projects_count : 0;
        map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => managerPressureScore(b) - managerPressureScore(a));
}

function filteredRiskLabel(summary = {}, activeCount = 0) {
    const score = safeNumber(summary.health_score);
    const scope = activeCount > 0 ? "Vista filtrada" : "Portafolio";
    if (score < 55) return `${scope} en riesgo alto`;
    if (score < 78) return `${scope} con riesgo medio`;
    return `${scope} estable`;
}

function filteredRiskMessage(summary = {}, activeCount = 0) {
    const scope = activeCount > 0 ? "La vista filtrada" : "El portafolio";
    return `${scope} muestra ${formatNumber(summary.total_projects)} proyecto(s), ${formatNumber(summary.red_projects)} en rojo, ${formatNumber(summary.open_tasks)} tarea(s) abierta(s) y ${formatNumber(summary.overdue_tasks)} vencida(s).`;
}

function buildFinanceFromSummary(summary = {}, fallback = {}) {
    return {
        budget_planned: summary.total_budget_planned ?? fallback.budget_planned,
        budget_actual: summary.total_budget_actual ?? fallback.budget_actual,
        contract_value: summary.total_contract_value ?? fallback.contract_value,
        pending_expenses: fallback.pending_expenses ?? 0,
    };
}

function ExecutiveDashboardFilters({ filters, setFilters, pmOptions = [], sponsorOptions = [], activeCount = 0, onClear }) {
    const inputStyle = {
        height: 42,
        borderRadius: 16,
        border: "1px solid #CBD5E1",
        background: "#FFFFFF",
        padding: "0 13px",
        color: "#0F172A",
        fontSize: 12,
        fontWeight: 800,
        outline: "none",
        width: "100%",
    };
    const labelStyle = { color: "#64748B", fontSize: 10, fontWeight: 950, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 };

    const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    return (
        <section style={{ marginBottom: 20, background: "rgba(255,255,255,.94)", border: "1px solid #E2E8F0", borderRadius: 24, padding: 18, boxShadow: "0 18px 45px rgba(15,23,42,.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
                <div>
                    <div style={{ color: "#2563EB", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".08em" }}>Filtros ejecutivos</div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>Filtra el tablero por proyecto, PM / Director, sponsor, salud y estado sin romper la lectura ejecutiva.</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <StatusPill tone={activeCount > 0 ? "info" : "neutral"}>{activeCount > 0 ? `${activeCount} filtro(s) activo(s)` : "Sin filtros"}</StatusPill>
                    <button type="button" style={secondaryButton} onClick={onClear}>Limpiar</button>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1.35fr) repeat(4, minmax(150px, .75fr))", gap: 12, alignItems: "end" }}>
                <div>
                    <div style={labelStyle}>Búsqueda</div>
                    <input
                        value={filters.search}
                        onChange={(e) => update("search", e.target.value)}
                        placeholder="Buscar proyecto, código, PM o sponsor..."
                        title="Busca por nombre de proyecto, código, PM / Director de proyecto o sponsor."
                        style={inputStyle}
                    />
                </div>
                <div>
                    <div style={labelStyle}>PM / Director</div>
                    <select value={filters.pm} onChange={(e) => update("pm", e.target.value)} title="Filtra proyectos por PM / Director de proyecto." style={inputStyle}>
                        <option value="all">Todos</option>
                        {pmOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                </div>
                <div>
                    <div style={labelStyle}>Sponsor</div>
                    <select value={filters.sponsor} onChange={(e) => update("sponsor", e.target.value)} title="Filtra proyectos por sponsor ejecutivo." style={inputStyle}>
                        <option value="all">Todos</option>
                        {sponsorOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                </div>
                <div>
                    <div style={labelStyle}>Salud</div>
                    <select value={filters.health} onChange={(e) => update("health", e.target.value)} title="Filtra por salud ejecutiva del proyecto." style={inputStyle}>
                        <option value="all">Todas</option>
                        <option value="rojo">Rojo</option>
                        <option value="amarillo">Amarillo</option>
                        <option value="verde">Verde</option>
                    </select>
                </div>
                <div>
                    <div style={labelStyle}>Estado</div>
                    <select value={filters.status} onChange={(e) => update("status", e.target.value)} title="Filtra por estado operativo del proyecto." style={inputStyle}>
                        <option value="all">Todos</option>
                        <option value="activo">Activo</option>
                        <option value="pausado">Pausado</option>
                        <option value="terminado">Terminado</option>
                        <option value="cancelado">Cancelado</option>
                    </select>
                </div>
            </div>
        </section>
    );
}

function ActiveFilterSummary({ filters, activeCount = 0, visibleProjects = 0, summary = {}, onClear }) {
    if (!activeCount) return null;

    const chips = [];
    if (String(filters.search || "").trim()) chips.push(`Búsqueda: ${filters.search.trim()}`);
    if (filters.pm && filters.pm !== "all") chips.push(`PM: ${filters.pm}`);
    if (filters.sponsor && filters.sponsor !== "all") chips.push(`Sponsor: ${filters.sponsor}`);
    if (filters.health && filters.health !== "all") chips.push(`Salud: ${label(filters.health)}`);
    if (filters.status && filters.status !== "all") chips.push(`Estado: ${label(filters.status)}`);

    return (
        <section style={{ marginBottom: 20, border: "1px solid #BFDBFE", background: "linear-gradient(135deg, #EFF6FF, #FFFFFF)", borderRadius: 22, padding: 16, boxShadow: "0 16px 38px rgba(37,99,235,.08)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                    <div style={{ color: "#1D4ED8", fontSize: 11, fontWeight: 950, letterSpacing: ".07em", textTransform: "uppercase" }}>Vista filtrada</div>
                    <div style={{ marginTop: 6, color: "#0F172A", fontSize: 16, fontWeight: 950 }}>
                        {formatNumber(visibleProjects)} proyecto(s) visible(s) · {formatNumber(summary.red_projects)} en rojo · {formatNumber(summary.overdue_tasks)} tarea(s) vencida(s)
                    </div>
                    <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {chips.map((chip) => <StatusPill key={chip} tone="info">{chip}</StatusPill>)}
                    </div>
                </div>
                <button type="button" style={secondaryButton} onClick={onClear}>Limpiar filtros</button>
            </div>
        </section>
    );
}

function PortfolioTable({ rows = [], onOpenProject }) {
    const columns = [
        {
            key: "project",
            label: "Proyecto",
            defaultSortDirection: "asc",
            sortValue: (row) => row.project_name || "",
            title: columnTooltips.proyecto,
            sortable: true,
            width: 240,
            render: (row) => (
                <div>
                    <div style={{ fontWeight: 950, color: "#0F172A" }}>{cleanText(row.project_name)}</div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 800 }}>{row.project_code || "SIN CÓDIGO"} · {label(row.stage)}</div>
                </div>
            ),
        },
        {
            key: "pm_sponsor",
            label: "PM / Sponsor",
            defaultSortDirection: "asc",
            sortValue: (row) => row.project_manager_name || row.sponsor_name || "",
            title: `${columnTooltips.pm} ${columnTooltips.sponsor}`,
            width: 210,
            render: (row) => (
                <div>
                    <div title={columnTooltips.pm} style={{ color: "#0F172A", fontSize: 12, fontWeight: 900, cursor: "help" }}>{row.project_manager_name || "Sin PM"}</div>
                    <div title={columnTooltips.sponsor} style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750, cursor: "help" }}>{row.sponsor_name || "Sin sponsor"}</div>
                </div>
            ),
        },
        {
            key: "health",
            label: "Salud",
            sortValue: (row) => ({ rojo: 3, amarillo: 2, verde: 1 }[String(row.health_status || "").toLowerCase()] || 0),
            title: columnTooltips.salud,
            nowrap: true,
            render: (row) => <StatusPill tone={toneFromHealth(row.health_status)} title={columnTooltips.salud}>{label(row.health_status)}</StatusPill>,
        },
        {
            key: "progress",
            label: "Avance",
            sortValue: (row) => safeNumber(row.progress_percent),
            title: columnTooltips.avance,
            width: 150,
            render: (row) => <ProgressBar value={row.progress_percent} color="#2563EB" rightLabel={`${formatNumber(row.progress_percent, 0)}%`} />,
        },
        {
            key: "tasks",
            label: "Tareas",
            sortValue: (row) => safeNumber(row.overdue_tasks) * 1000 + safeNumber(row.open_tasks) * 10 + safeNumber(row.total_tasks),
            title: columnTooltips.tareas,
            width: 180,
            render: (row) => (
                <div>
                    <div style={{ fontWeight: 900, color: "#0F172A" }}>{formatNumber(row.total_tasks)} total</div>
                    <div style={{ color: "#64748B", fontSize: 11, fontWeight: 750 }}>{formatNumber(row.open_tasks)} abiertas · {formatNumber(row.overdue_tasks)} vencidas</div>
                </div>
            ),
        },
        {
            key: "evidence",
            label: "Evidencia",
            sortValue: (row) => safeNumber(row.evidence_total) + safeNumber(row.geo_evidence_total) * 0.1 - safeNumber(row.out_of_range_total) * 3,
            title: columnTooltips.evidencia,
            width: 220,
            render: (row) => (
                <div>
                    <div style={{ fontWeight: 900, color: "#0F172A" }}>{formatNumber(row.evidence_total)} evidencias</div>
                    <div style={{ color: "#64748B", fontSize: 11, fontWeight: 750 }}>{formatNumber(row.geo_evidence_total)} con geo · {formatNumber(row.out_of_range_total)} fuera de rango</div>
                </div>
            ),
        },
        {
            key: "score",
            label: "Score PMO",
            sortValue: (row) => safeNumber(row.executive_score),
            title: columnTooltips.score,
            nowrap: true,
            render: (row) => <StatusPill tone={toneFromExecutive(row.executive_tone)} title={columnTooltips.score}>{formatNumber(row.executive_score)}</StatusPill>,
        },
        {
            key: "action",
            label: "Acción",
            sortable: false,
            title: columnTooltips.accion,
            nowrap: true,
            render: (row) => <button type="button" onClick={(e) => { e.stopPropagation(); onOpenProject?.(row.project_id); }} style={secondaryButton}>Ver ficha 360</button>,
        },
    ];

    return (
        <ExecutiveGrid
            rows={rows}
            columns={columns}
            minWidth={1120}
            getKey={(row) => row.project_id}
            onRowClick={(row) => onOpenProject?.(row.project_id)}
            emptyText="No hay proyectos en esta selección. Ajusta filtros o limpia la vista para recuperar el portafolio ejecutivo."
            defaultSortKey="score"
            defaultSortDirection="desc"
        />
    );
}

function Th({ children }) {
    return <th style={{ textAlign: "left", padding: "12px 14px", fontSize: 11, color: "#64748B", fontWeight: 950, textTransform: "uppercase", borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap" }}>{children}</th>;
}

function Td({ children }) {
    return <td style={{ padding: "13px 14px", fontSize: 12, color: "#334155", verticalAlign: "top", borderTop: "1px solid #E2E8F0" }}>{children}</td>;
}


const columnTooltips = {
    proyecto: "Proyecto dentro del portafolio. Abre la ficha 360 para revisar alcance, tareas, riesgos, evidencias y decisiones.",
    pm: "PM / Director de proyecto responsable de dirigir, coordinar ejecución, controlar riesgos, fechas y entregables.",
    sponsor: "Sponsor ejecutivo que respalda prioridades, recursos y decisiones estratégicas del proyecto.",
    salud: "Lectura ejecutiva del estado del proyecto: verde, amarillo o rojo según señales de riesgo y seguimiento.",
    avance: "Avance operativo del proyecto o persona. El color de la barra indica progreso, no necesariamente salud.",
    tareas: "Carga operativa: tareas totales, abiertas, vencidas, bloqueadas o en revisión según el contexto.",
    evidencia: "Trazabilidad documental y geográfica: evidencias cargadas, con ubicación y fuera de rango.",
    score: scorePmoTooltip(),
    accion: "Acción disponible para abrir detalle, filtrar información o revisar el elemento seleccionado.",
    responsable: "Usuario encargado de ejecutar la tarea y documentar avance, comentario y evidencia.",
    revisor: "Usuario encargado de validar, aprobar, rechazar o solicitar cambios sobre avances enviados.",
};

function pressureTone(value) {
    const n = safeNumber(value);
    if (n >= 70) return "danger";
    if (n >= 35) return "warning";
    if (n > 0) return "info";
    return "success";
}

function managerDirectionScore(row = {}) {
    const avg = safeNumber(row.avg_progress);
    const red = safeNumber(row.red_projects);
    const overdue = safeNumber(row.overdue_tasks);
    const blocked = safeNumber(row.blocked_tasks);
    return clamp(avg - red * 10 - overdue * 6 - blocked * 4);
}

function sponsorPressureScore(row = {}) {
    return clamp(safeNumber(row.red_projects) * 25 + safeNumber(row.overdue_tasks) * 8 + safeNumber(row.projects_count) * 2);
}

function projectPressureScore(row = {}) {
    const explicit = safeNumber(row.executive_score);
    if (explicit > 0) return clamp(explicit);
    const healthPenalty = String(row.health_status || '').toLowerCase() === 'rojo' ? 30 : String(row.health_status || '').toLowerCase() === 'amarillo' ? 14 : 0;
    return clamp(
        healthPenalty +
        safeNumber(row.overdue_tasks) * 12 +
        safeNumber(row.open_tasks) * 2 +
        safeNumber(row.out_of_range_total) * 8 +
        safeNumber(row.high_open_risks) * 10 +
        safeNumber(row.overdue_deliverables) * 12
    );
}

function managerPressureScore(row = {}) {
    return clamp(
        safeNumber(row.red_projects) * 24 +
        safeNumber(row.overdue_tasks) * 8 +
        safeNumber(row.blocked_tasks) * 6 +
        Math.max(0, 100 - safeNumber(row.avg_progress)) * 0.35 +
        safeNumber(row.projects_count) * 2
    );
}

function ExecutiveGrid({ columns = [], rows = [], getKey, onRowClick, emptyText = "Sin información disponible.", minWidth = 900, getRowTitle, defaultSortKey = null, defaultSortDirection = "desc" }) {
    const [hoveredKey, setHoveredKey] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: defaultSortKey, direction: defaultSortDirection });

    const resolvedMinWidth = typeof minWidth === "number" ? `${minWidth}px` : (minWidth || "100%");

    const resolveSortValue = (row, col) => {
        if (typeof col.sortValue === "function") return col.sortValue(row);

        const direct = row?.[col.key];
        if (direct !== undefined && direct !== null && direct !== "") return direct;

        switch (col.key) {
            case "project":
            case "task":
                return row.project_name || row.task_name || row.name || "";
            case "pm_sponsor":
                return row.project_manager_name || row.sponsor_name || "";
            case "health": {
                const order = { rojo: 3, amarillo: 2, verde: 1 };
                return order[String(row.health_status || "").toLowerCase()] || 0;
            }
            case "progress":
            case "avg_progress":
                return safeNumber(row.progress_percent ?? row.avg_progress);
            case "tasks":
                return safeNumber(row.overdue_tasks) * 1000 + safeNumber(row.open_tasks) * 10 + safeNumber(row.total_tasks);
            case "evidence":
                return safeNumber(row.evidence_total) + safeNumber(row.geo_evidence_total) * 0.1 - safeNumber(row.out_of_range_total) * 3;
            case "score":
            case "pressure":
                return safeNumber(row.executive_score ?? row.pressure_score ?? managerDirectionScore(row));
            case "close_rate":
                return safeNumber(row.completed_tasks) / Math.max(1, safeNumber(row.assigned_tasks)) * 100;
            case "due_date":
                return row.due_date ? new Date(`${row.due_date}T00:00:00`).getTime() : 0;
            case "criticality_score":
                return safeNumber(row.days_overdue) * 1000 + safeNumber(row.criticality_score);
            default:
                return direct ?? "";
        }
    };

    const sortedRows = useMemo(() => {
        if (!sortConfig?.key) return rows;

        const col = columns.find((c) => c.key === sortConfig.key);
        if (!col) return rows;

        const direction = sortConfig.direction === "asc" ? 1 : -1;

        return [...rows].sort((a, b) => {
            const av = resolveSortValue(a, col);
            const bv = resolveSortValue(b, col);

            if (typeof av === "number" || typeof bv === "number") {
                return (safeNumber(av) - safeNumber(bv)) * direction;
            }

            return String(av || "").localeCompare(String(bv || ""), "es", { numeric: true, sensitivity: "base" }) * direction;
        });
    }, [rows, columns, sortConfig]);

    const toggleSort = (col) => {
        if (col.sortable === false || col.key === "action") return;
        setSortConfig((current) => {
            if (current?.key === col.key) {
                return { key: col.key, direction: current.direction === "asc" ? "desc" : "asc" };
            }
            return { key: col.key, direction: col.defaultSortDirection || "desc" };
        });
    };

    if (!rows.length) return <EmptyState text={emptyText} />;

    return (
        <div style={{ overflowX: resolvedMinWidth === "100%" ? "hidden" : "auto", border: "1px solid #E2E8F0", borderRadius: 18, background: "#FFFFFF" }}>
            <table style={{ width: "100%", minWidth: resolvedMinWidth, borderCollapse: "separate", borderSpacing: 0, background: "#FFFFFF", tableLayout: resolvedMinWidth === "100%" ? "auto" : "fixed" }}>
                <thead>
                    <tr style={{ background: "#F8FAFC" }}>
                        {columns.map((col) => {
                            const canSort = col.sortable !== false && col.key !== "action";
                            const activeSort = sortConfig?.key === col.key;
                            const sortIcon = activeSort ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕";

                            return (
                                <th
                                    key={col.key}
                                    title={col.title ? `${col.title}${canSort ? "\nDa clic para ordenar esta columna." : ""}` : (canSort ? "Da clic para ordenar esta columna." : undefined)}
                                    onClick={() => toggleSort(col)}
                                    style={{
                                        textAlign: "left",
                                        padding: "12px 14px",
                                        fontSize: 10,
                                        color: activeSort ? "#0F172A" : "#64748B",
                                        fontWeight: 950,
                                        textTransform: "uppercase",
                                        borderBottom: "1px solid #E2E8F0",
                                        whiteSpace: "nowrap",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                        background: activeSort ? "#EFF6FF" : "#F8FAFC",
                                        cursor: canSort ? "pointer" : (col.title ? "help" : "default"),
                                        width: col.width,
                                        userSelect: "none",
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        {col.label}
                                        {canSort ? <span style={{ color: activeSort ? "#2563EB" : "#94A3B8", fontSize: 11 }}>{sortIcon}</span> : null}
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row, idx) => {
                        const key = getKey ? getKey(row, idx) : (row.id || row.project_id || row.user_id || idx);
                        const isSelected = selectedKey === key;
                        const isHovered = hoveredKey === key;
                        const baseBg = idx % 2 === 0 ? "#FFFFFF" : "#F8FAFC";
                        const bg = isSelected ? "#EFF6FF" : isHovered ? "#EAF2FF" : baseBg;

                        return (
                            <tr
                                key={key}
                                title={getRowTitle ? getRowTitle(row) : (onRowClick ? "Da clic para abrir el detalle operativo." : undefined)}
                                onMouseEnter={() => setHoveredKey(key)}
                                onMouseLeave={() => setHoveredKey(null)}
                                onClick={() => {
                                    setSelectedKey(key);
                                    onRowClick?.(row);
                                }}
                                style={{
                                    background: bg,
                                    cursor: onRowClick ? "pointer" : "default",
                                    transition: "background .14s ease, box-shadow .14s ease",
                                    boxShadow: isSelected ? "inset 4px 0 0 #2563EB" : "inset 4px 0 0 transparent",
                                }}
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        style={{
                                            padding: "13px 14px",
                                            fontSize: 12,
                                            color: "#334155",
                                            verticalAlign: "top",
                                            borderTop: "1px solid #E2E8F0",
                                            whiteSpace: col.nowrap ? "nowrap" : "normal",
                                        }}
                                    >
                                        {col.render ? col.render(row, idx) : row[col.key]}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


function RankingList({ rows = [], mode = "responsibles" }) {
    if (!rows.length) return <EmptyState text="Sin información suficiente de usuarios." />;
    return (
        <div style={{ display: "grid", gap: 12 }}>
            {rows.slice(0, 6).map((row, idx) => {
                const isReviewer = mode === "reviewers";
                const pressure = isReviewer ? safeNumber(row.pending_reviews) : safeNumber(row.overdue_tasks);
                const tone = pressure > 0 ? "warning" : "success";
                return (
                    <div key={`${mode}-${row.user_id || idx}`} style={{ border: "1px solid #E2E8F0", borderRadius: 18, background: "#FFFFFF", padding: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>{row.name || `Usuario #${row.user_id}`}</div>
                                <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>
                                    {isReviewer
                                        ? `${formatNumber(row.assigned_reviews)} revisiones · ${formatNumber(row.approved_reviews)} aprobadas`
                                        : `${formatNumber(row.assigned_tasks)} asignadas · ${formatNumber(row.completed_tasks)} terminadas`}
                                </div>
                            </div>
                            <StatusPill tone={tone}>{isReviewer ? `${formatNumber(row.pending_reviews)} pendientes` : `${formatNumber(row.overdue_tasks)} vencidas`}</StatusPill>
                        </div>
                        {!isReviewer ? <ProgressBar value={(safeNumber(row.completed_tasks) / Math.max(1, safeNumber(row.assigned_tasks))) * 100} color={toneMap[tone].accent} label="Tasa de cierre" rightLabel={`${formatNumber((safeNumber(row.completed_tasks) / Math.max(1, safeNumber(row.assigned_tasks))) * 100, 0)}%`} /> : null}
                    </div>
                );
            })}
        </div>
    );
}


function RoleAccountabilityMatrix({ projectManagers = [], sponsors = [], responsibles = [], reviewers = [], onOpenPM, onOpenSponsor }) {
    const pmColumns = [
        {
            key: "name",
            label: "PM / Director de proyecto",
            title: "PM responsable de dirigir proyectos, coordinar ejecución, anticipar riesgos y escalar decisiones.",
            width: 220,
            render: (row) => (
                <div>
                    <div style={{ color: "#0F172A", fontWeight: 950 }}>{row.name || "Sin PM"}</div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>Dirección de proyectos</div>
                </div>
            ),
        },
        { key: "projects_count", label: "Proyectos", title: "Cantidad de proyectos bajo dirección del PM.", nowrap: true, render: (row) => formatNumber(row.projects_count) },
        { key: "red_projects", label: "En rojo", title: "Proyectos con salud roja bajo este PM.", nowrap: true, render: (row) => <StatusPill tone={safeNumber(row.red_projects) > 0 ? "danger" : "success"}>{formatNumber(row.red_projects)}</StatusPill> },
        { key: "avg_progress", label: "Avance prom.", title: "Avance promedio de los proyectos dirigidos por este PM.", width: 160, render: (row) => <ProgressBar value={row.avg_progress} color="#2563EB" rightLabel={`${formatNumber(row.avg_progress, 0)}%`} /> },
        { key: "overdue_tasks", label: "Vencidas", title: "Tareas vencidas dentro de los proyectos dirigidos por este PM.", nowrap: true, render: (row) => <StatusPill tone={safeNumber(row.overdue_tasks) > 0 ? "warning" : "success"}>{formatNumber(row.overdue_tasks)}</StatusPill> },
        {
            key: "score", label: "Score PMO", title: scorePmoTooltip(), nowrap: true, render: (row) => {
                const score = managerDirectionScore(row);
                return <StatusPill tone={pressureTone(100 - score)}>{formatNumber(score, 0)}</StatusPill>;
            }
        },
    ];

    const sponsorColumns = [
        {
            key: "name",
            label: "Sponsor",
            title: "Figura ejecutiva que respalda prioridades, recursos y decisiones estratégicas.",
            width: 220,
            render: (row) => (
                <div>
                    <div style={{ color: "#0F172A", fontWeight: 950 }}>{row.name || "Sin sponsor"}</div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>Patrocinio ejecutivo</div>
                </div>
            ),
        },
        { key: "projects_count", label: "Proyectos", title: "Cantidad de proyectos patrocinados.", nowrap: true, render: (row) => formatNumber(row.projects_count) },
        { key: "red_projects", label: "En riesgo", title: "Proyectos rojos o con presión bajo este sponsor.", nowrap: true, render: (row) => <StatusPill tone={safeNumber(row.red_projects) > 0 ? "danger" : "success"}>{formatNumber(row.red_projects)}</StatusPill> },
        { key: "contract_value", label: "Valor cartera", title: "Valor contractual estimado de los proyectos patrocinados.", nowrap: true, render: (row) => formatMoney(row.contract_value, row.currency || "MXN") },
        {
            key: "pressure", label: "Score PMO", title: "Score PMO del sponsor: presión ejecutiva estimada por proyectos en riesgo, cartera y tareas vencidas.", nowrap: true, render: (row) => {
                const score = sponsorPressureScore(row);
                return <StatusPill tone={pressureTone(score)}>{formatNumber(score, 0)}</StatusPill>;
            }
        },
    ];

    const responsibleColumns = [
        { key: "name", label: "Responsable", title: columnTooltips.responsable, width: 220, render: (row) => <strong style={{ color: "#0F172A" }}>{row.name || "Sin responsable"}</strong> },
        { key: "assigned_tasks", label: "Asignadas", title: "Tareas asignadas al responsable.", nowrap: true, render: (row) => formatNumber(row.assigned_tasks) },
        { key: "completed_tasks", label: "Terminadas", title: "Tareas cerradas o terminadas por el responsable.", nowrap: true, render: (row) => formatNumber(row.completed_tasks) },
        { key: "open_tasks", label: "Abiertas", title: "Tareas abiertas que aún requieren avance, corrección o seguimiento.", nowrap: true, render: (row) => formatNumber(row.open_tasks) },
        { key: "overdue_tasks", label: "Vencidas", title: "Tareas vencidas asignadas al responsable.", nowrap: true, render: (row) => <StatusPill tone={safeNumber(row.overdue_tasks) > 0 ? "warning" : "success"}>{formatNumber(row.overdue_tasks)}</StatusPill> },
        {
            key: "close_rate", label: "Cierre", title: "Porcentaje de cierre de tareas asignadas.", width: 150, render: (row) => {
                const rate = safeNumber(row.completed_tasks) / Math.max(1, safeNumber(row.assigned_tasks)) * 100;
                return <ProgressBar value={rate} color="#10B981" rightLabel={`${formatNumber(rate, 0)}%`} />;
            }
        },
    ];

    const reviewerColumns = [
        { key: "name", label: "Revisor", title: columnTooltips.revisor, width: 220, render: (row) => <strong style={{ color: "#0F172A" }}>{row.name || "Sin revisor"}</strong> },
        { key: "assigned_reviews", label: "Revisiones", title: "Cantidad de tareas asignadas a revisión.", nowrap: true, render: (row) => formatNumber(row.assigned_reviews) },
        { key: "pending_reviews", label: "Pendientes", title: "Revisiones pendientes de dictamen.", nowrap: true, render: (row) => <StatusPill tone={safeNumber(row.pending_reviews) > 0 ? "purple" : "success"}>{formatNumber(row.pending_reviews)}</StatusPill> },
        { key: "approved_reviews", label: "Aprobadas", title: "Dictámenes aprobados por el revisor.", nowrap: true, render: (row) => formatNumber(row.approved_reviews) },
        { key: "change_reviews", label: "Cambios", title: "Solicitudes de cambios o correcciones generadas por el revisor.", nowrap: true, render: (row) => <StatusPill tone={safeNumber(row.change_reviews) > 0 ? "warning" : "success"}>{formatNumber(row.change_reviews)}</StatusPill> },
    ];

    return (
        <Block title="Accountability PMO por rol" subtitle="Separa dirección, patrocinio, ejecución y validación. Cada rol tiene misión, presión y resultado diferente.">
            <div style={{ display: "grid", gap: 22 }}>
                <div>
                    <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-end" }}>
                        <div>
                            <div style={{ color: "#2563EB", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>Dirección de proyectos</div>
                            <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>PMs y sponsors concentran dirección, prioridades, recursos y desbloqueo ejecutivo.</div>
                        </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, .85fr)", gap: 18 }}>
                        <div>
                            <div style={{ marginBottom: 10 }}>
                                <h3 title="PM / Directores de proyecto: dirigen proyectos, coordinan ejecución, anticipan riesgos, controlan fechas y escalan decisiones." style={{ margin: 0, color: "#0F172A", fontSize: 14, fontWeight: 950, cursor: "help" }}>PM / Directores de proyecto</h3>
                                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>Quién dirige proyectos, concentra presión y debe desbloquear ejecución.</div>
                            </div>
                            <ExecutiveGrid rows={projectManagers} columns={pmColumns} minWidth="100%" getKey={(row, idx) => `pm-${row.user_id || idx}`} onRowClick={onOpenPM} getRowTitle={(row) => `Abrir proyectos dirigidos por ${row.name || "este PM"}.`} emptyText="No hay PM / Directores de proyecto en esta selección. Ajusta filtros o asigna PM a los proyectos para habilitar esta lectura." />
                        </div>
                        <div>
                            <div style={{ marginBottom: 10 }}>
                                <h3 title="Sponsors: respaldan prioridades, recursos, decisiones estratégicas y valor del proyecto." style={{ margin: 0, color: "#0F172A", fontSize: 14, fontWeight: 950, cursor: "help" }}>Sponsors</h3>
                                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>Quién respalda decisiones, prioridades y valor estratégico.</div>
                            </div>
                            <ExecutiveGrid rows={sponsors} columns={sponsorColumns} minWidth="100%" getKey={(row, idx) => `sp-${row.user_id || row.name || idx}`} onRowClick={onOpenSponsor} getRowTitle={(row) => `Abrir proyectos patrocinados por ${row.name || "este sponsor"}.`} emptyText="No hay sponsors en esta selección. Ajusta filtros o asigna sponsor ejecutivo a los proyectos." />
                        </div>
                    </div>
                </div>

                <div>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ color: "#2563EB", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>Ejecución y validación</div>
                        <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>Responsables y revisores convierten la estrategia en avances, evidencia, dictámenes y cierre.</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 18 }}>
                        <div>
                            <div style={{ marginBottom: 10 }}>
                                <h3 title="Responsables: ejecutan tareas, documentan avances, cargan evidencia y corrigen entregas." style={{ margin: 0, color: "#0F172A", fontSize: 14, fontWeight: 950, cursor: "help" }}>Responsables de tarea</h3>
                                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>Quién ejecuta, documenta y entrega avances.</div>
                            </div>
                            <ExecutiveGrid rows={responsibles} columns={responsibleColumns} minWidth="100%" getKey={(row, idx) => `owner-${row.user_id || idx}`} getRowTitle={(row) => `Lectura operativa de ${row.name || "este responsable"}.`} emptyText="No hay responsables de tarea en esta selección. Ajusta filtros o asigna responsables para medir ejecución." />
                        </div>
                        <div>
                            <div style={{ marginBottom: 10 }}>
                                <h3 title="Revisores: validan evidencia, aprueban, rechazan o solicitan cambios para mantener calidad." style={{ margin: 0, color: "#0F172A", fontSize: 14, fontWeight: 950, cursor: "help" }}>Revisores</h3>
                                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>Quién valida, aprueba o solicita correcciones.</div>
                            </div>
                            <ExecutiveGrid rows={reviewers} columns={reviewerColumns} minWidth="100%" getKey={(row, idx) => `reviewer-${row.user_id || idx}`} getRowTitle={(row) => `Lectura de carga de revisión de ${row.name || "este revisor"}.`} emptyText="No hay revisores en esta selección. Ajusta filtros o asigna revisores para medir validación y calidad." />
                        </div>
                    </div>
                </div>
            </div>
        </Block>
    );
}

function PressureAnalysisBlock({ projects = [], projectManagers = [], onOpenProject, onOpenPM }) {
    const [view, setView] = useState("projects");
    const [query, setQuery] = useState("");
    const [healthFilter, setHealthFilter] = useState("all");
    const [sortMode, setSortMode] = useState("pressure_desc");

    const normalizedQuery = query.trim().toLowerCase();

    const compareRows = (a, b) => {
        if (sortMode === "name_asc") {
            const an = String(a.project_name || a.name || "").toLowerCase();
            const bn = String(b.project_name || b.name || "").toLowerCase();
            return an.localeCompare(bn);
        }
        if (sortMode === "projects_desc") return safeNumber(b.projects_count) - safeNumber(a.projects_count);
        if (sortMode === "red_desc") return safeNumber(b.red_projects) - safeNumber(a.red_projects);
        if (sortMode === "overdue_desc") return safeNumber(b.overdue_tasks) - safeNumber(a.overdue_tasks);
        if (sortMode === "progress_asc") return safeNumber(a.avg_progress || a.progress_percent) - safeNumber(b.avg_progress || b.progress_percent);
        return safeNumber(b.pressure_score) - safeNumber(a.pressure_score);
    };

    const projectRows = useMemo(() => {
        return [...projects]
            .map((row) => ({ ...row, pressure_score: projectPressureScore(row) }))
            .filter((row) => {
                const matchesQuery = !normalizedQuery || [row.project_name, row.project_code, row.project_manager_name, row.sponsor_name]
                    .some((value) => String(value || "").toLowerCase().includes(normalizedQuery));
                const matchesHealth = healthFilter === "all" || String(row.health_status || "").toLowerCase() === healthFilter;
                return matchesQuery && matchesHealth;
            })
            .sort(compareRows)
            .slice(0, 8);
    }, [projects, normalizedQuery, healthFilter, sortMode]);

    const pmRows = useMemo(() => {
        return [...projectManagers]
            .map((row) => ({ ...row, pressure_score: managerPressureScore(row) }))
            .filter((row) => !normalizedQuery || String(row.name || "").toLowerCase().includes(normalizedQuery))
            .sort(compareRows)
            .slice(0, 8);
    }, [projectManagers, normalizedQuery, sortMode]);

    const isProjects = view === "projects";
    const rows = isProjects ? projectRows : pmRows;
    const topRow = rows[0] || null;
    const criticalCount = rows.filter((row) => safeNumber(row.pressure_score) >= 70).length;
    const totalOpen = isProjects
        ? rows.reduce((acc, row) => acc + safeNumber(row.open_tasks), 0)
        : rows.reduce((acc, row) => acc + safeNumber(row.projects_count), 0);
    const totalOverdue = isProjects
        ? rows.reduce((acc, row) => acc + safeNumber(row.overdue_tasks), 0)
        : rows.reduce((acc, row) => acc + safeNumber(row.overdue_tasks), 0);
    const maxScore = Math.max(1, ...rows.map((row) => safeNumber(row.pressure_score)));

    const sortOptions = isProjects
        ? [
            { value: "pressure_desc", label: "Mayor presión" },
            { value: "overdue_desc", label: "Más vencidas" },
            { value: "progress_asc", label: "Menor avance" },
            { value: "name_asc", label: "Nombre A-Z" },
        ]
        : [
            { value: "pressure_desc", label: "Mayor presión" },
            { value: "projects_desc", label: "Más proyectos" },
            { value: "red_desc", label: "Más rojos" },
            { value: "overdue_desc", label: "Más vencidas" },
            { value: "name_asc", label: "Nombre A-Z" },
        ];

    return (
        <Block
            title="Análisis de presión"
            subtitle="Compara proyectos y PM / Directores con filtros, ordenamiento y lectura gráfica de presión PMO."
            action={
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: 4, borderRadius: 999, background: "#F8FAFC", border: "1px solid #CBD5E1" }}>
                    {[{ key: "projects", label: "Proyectos" }, { key: "pms", label: "PMs" }].map((option) => {
                        const active = view === option.key;
                        return (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => { setView(option.key); setHealthFilter("all"); setSortMode("pressure_desc"); }}
                                style={{
                                    height: 32,
                                    padding: "0 14px",
                                    borderRadius: 999,
                                    border: active ? "1px solid #0F172A" : "1px solid transparent",
                                    background: active ? "#0F172A" : "transparent",
                                    color: active ? "#FFFFFF" : "#334155",
                                    fontSize: 11,
                                    fontWeight: 950,
                                    cursor: "pointer",
                                }}
                                title={option.key === "projects" ? "Ver presión comparativa por proyecto." : "Ver presión comparativa por PM / Director de proyecto."}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            }
        >
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, .9fr)", gap: 18, alignItems: "stretch" }}>
                <div style={{ borderRadius: 24, padding: 20, background: "linear-gradient(135deg, rgba(15,23,42,.98), rgba(30,41,59,.96))", color: "#FFFFFF", border: "1px solid rgba(148,163,184,.22)", boxShadow: "0 22px 50px rgba(15,23,42,.18)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ color: "#93C5FD", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>{isProjects ? "Presión por proyecto" : "Presión por PM"}</div>
                            <div style={{ marginTop: 6, fontSize: 26, fontWeight: 950, letterSpacing: "-.04em" }}>{topRow ? (isProjects ? cleanText(topRow.project_name) : topRow.name) : "Sin datos"}</div>
                            <div style={{ marginTop: 4, color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>
                                {topRow
                                    ? (isProjects
                                        ? `${topRow.project_code || 'PRJ'} · ${formatNumber(topRow.open_tasks)} abiertas · ${formatNumber(topRow.overdue_tasks)} vencidas`
                                        : `${formatNumber(topRow.projects_count)} proyecto(s) · ${formatNumber(topRow.red_projects)} en rojo · ${formatNumber(topRow.overdue_tasks)} vencidas`)
                                    : "No se detecta presión dominante en este momento."}
                            </div>
                        </div>
                        <div style={{ minWidth: 118, minHeight: 118, borderRadius: 999, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", boxShadow: "inset 0 0 0 10px rgba(255,255,255,.03)" }} title={scorePmoTooltip()}>
                            <div style={{ color: "#F59E0B", fontSize: 13, fontWeight: 900 }}>Score PMO</div>
                            <div style={{ marginTop: 4, fontSize: 34, fontWeight: 950 }}>{topRow ? formatNumber(topRow.pressure_score, 0) : 0}</div>
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginTop: 18 }}>
                        {[
                            { label: isProjects ? "Críticos" : "PM críticos", value: criticalCount, help: "Señales con presión alta" },
                            { label: isProjects ? "Carga abierta" : "Proyectos visibles", value: totalOpen, help: isProjects ? "Tareas abiertas de los proyectos mostrados" : "Proyectos a cargo de los PM mostrados" },
                            { label: "Vencidas", value: totalOverdue, help: "Tareas vencidas bajo seguimiento" },
                        ].map((item) => (
                            <div key={item.label} title={item.help} style={{ padding: 14, borderRadius: 18, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)", cursor: "help" }}>
                                <div style={{ color: "#94A3B8", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>{item.label}</div>
                                <div style={{ marginTop: 8, fontSize: 24, fontWeight: 950 }}>{formatNumber(item.value)}</div>
                                <div style={{ marginTop: 4, color: "#CBD5E1", fontSize: 11, fontWeight: 700 }}>{item.help}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isProjects ? "minmax(0,1fr) 150px 150px" : "minmax(0,1fr) 170px", gap: 10, marginTop: 18 }}>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={isProjects ? "Buscar proyecto, PM o sponsor..." : "Buscar PM / Director..."}
                            style={{ height: 38, borderRadius: 999, border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.08)", color: "#FFFFFF", padding: "0 14px", fontSize: 12, fontWeight: 800, outline: "none" }}
                            title="Filtra la gráfica sin cambiar el resto del dashboard."
                        />
                        {isProjects ? (
                            <select value={healthFilter} onChange={(e) => setHealthFilter(e.target.value)} style={{ height: 38, borderRadius: 999, border: "1px solid rgba(255,255,255,.18)", background: "rgba(15,23,42,.85)", color: "#FFFFFF", padding: "0 12px", fontSize: 12, fontWeight: 850 }} title="Filtrar proyectos por salud ejecutiva.">
                                <option value="all">Todas</option>
                                <option value="rojo">Rojo</option>
                                <option value="amarillo">Amarillo</option>
                                <option value="verde">Verde</option>
                            </select>
                        ) : null}
                        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} style={{ height: 38, borderRadius: 999, border: "1px solid rgba(255,255,255,.18)", background: "rgba(15,23,42,.85)", color: "#FFFFFF", padding: "0 12px", fontSize: 12, fontWeight: 850 }} title="Ordenamiento real de la gráfica actual.">
                            {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                    </div>
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                    {rows.length ? rows.slice(0, 5).map((row, idx) => {
                        const score = safeNumber(row.pressure_score);
                        const pct = Math.max(10, (score / maxScore) * 100);
                        const tone = pressureTone(score);
                        const s = toneMap[tone] || toneMap.neutral;
                        const title = isProjects ? cleanText(row.project_name) : row.name;
                        const subtitle = isProjects
                            ? `${row.project_code || 'PRJ'} · ${formatNumber(row.open_tasks)} abiertas · ${formatNumber(row.overdue_tasks)} vencidas`
                            : `${formatNumber(row.projects_count)} proyecto(s) · ${formatNumber(row.red_projects)} en rojo · ${formatNumber(row.overdue_tasks)} vencidas`;
                        return (
                            <button
                                key={isProjects ? row.project_id : row.user_id || `${row.name}-${idx}`}
                                type="button"
                                onClick={() => isProjects ? onOpenProject?.(row.project_id) : onOpenPM?.(row)}
                                style={{
                                    border: `1px solid ${s.border}`,
                                    background: "#FFFFFF",
                                    borderRadius: 18,
                                    padding: 14,
                                    cursor: "pointer",
                                    textAlign: "left",
                                    boxShadow: `0 14px 32px ${s.glow}`,
                                }}
                                title={isProjects ? "Abrir ficha 360 del proyecto." : "Abrir lectura de proyectos del PM / Director."}
                            >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                        <div style={{ width: 28, height: 28, borderRadius: 999, background: s.soft, color: s.value, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 950, flexShrink: 0 }}>{idx + 1}</div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
                                            <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitle}</div>
                                        </div>
                                    </div>
                                    <StatusPill tone={tone} title={scorePmoTooltip()}>{formatNumber(score, 0)}</StatusPill>
                                </div>
                                <div style={{ marginTop: 12 }}>
                                    <div style={{ height: 10, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                                        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: `linear-gradient(90deg, ${s.accent}, ${s.value})` }} />
                                    </div>
                                </div>
                                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {isProjects ? (
                                        <>
                                            <StatusPill tone={toneFromHealth(row.health_status)} title="Salud actual del proyecto.">{label(row.health_status)}</StatusPill>
                                            <StatusPill tone={safeNumber(row.overdue_tasks) > 0 ? "danger" : "success"} title="Tareas vencidas del proyecto.">{formatNumber(row.overdue_tasks)} vencidas</StatusPill>
                                        </>
                                    ) : (
                                        <>
                                            <StatusPill tone={safeNumber(row.red_projects) > 0 ? "danger" : "success"} title="Proyectos en rojo bajo este PM.">{formatNumber(row.red_projects)} en rojo</StatusPill>
                                            <StatusPill tone={safeNumber(row.overdue_tasks) > 0 ? "warning" : "success"} title="Tareas vencidas bajo proyectos de este PM.">{formatNumber(row.overdue_tasks)} vencidas</StatusPill>
                                        </>
                                    )}
                                </div>
                            </button>
                        );
                    }) : <EmptyState text={isProjects ? "No hay proyectos para analizar con los filtros actuales." : "No hay PMs para analizar con los filtros actuales."} />}
                </div>
            </div>
        </Block>
    );
}

function CompactMetricGrid({ items = [] }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 12 }}>
            {items.map((item) => {
                const tone = item.tone || "neutral";
                const s = toneMap[tone] || toneMap.neutral;
                return (
                    <div key={item.label} style={{ border: `1px solid ${s.border}`, background: s.bg, borderRadius: 18, padding: 14 }}>
                        <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>{item.label}</div>
                        <div style={{ marginTop: 7, color: s.value, fontSize: 24, fontWeight: 950, letterSpacing: "-.04em" }}>{item.value}</div>
                        {item.help ? <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 700 }}>{item.help}</div> : null}
                    </div>
                );
            })}
        </div>
    );
}

const secondaryButton = {
    height: 34,
    padding: "0 13px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#0F172A",
    fontSize: 11,
    fontWeight: 950,
    cursor: "pointer",
};

const primaryButton = {
    height: 38,
    padding: "0 15px",
    borderRadius: 999,
    border: "1px solid #0F172A",
    background: "#0F172A",
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(15,23,42,.16)",
};

export default function DashboardEjecutivoPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState(() => parseDashboardFiltersFromSearch(window.location.search));

    const goProjects = (params = {}) => {
        const qp = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && String(value).trim() !== "") qp.set(key, String(value));
        });
        navigate(`/projects${qp.toString() ? `?${qp.toString()}` : ""}`);
    };

    const goMyWork = (filter = "todas") => {
        const qp = new URLSearchParams();
        qp.set("filter", filter);
        navigate(`/my-work?${qp.toString()}`);
    };

    const goDecisionCenter = () => navigate("/decision-center");
    const goProjectDetail = (projectId) => projectId && navigate(`/projects/${projectId}`);
    const goTaskDetail = (taskId, projectId) => {
        if (!taskId) return;
        const qp = new URLSearchParams();
        if (projectId) {
            qp.set("return_to", `/projects/${projectId}`);
            qp.set("return_label", "Dashboard PMO");
        }
        navigate(`/my-work/task/${taskId}${qp.toString() ? `?${qp.toString()}` : ""}`);
    };

    useEffect(() => {
        const nextFilters = parseDashboardFiltersFromSearch(location.search);
        setFilters((prev) => dashboardFiltersEqual(prev, nextFilters) ? prev : nextFilters);
    }, [location.search]);

    useEffect(() => {
        const nextSearch = applyDashboardFiltersToSearch(location.search, filters);
        const currentSearch = String(location.search || "").replace(/^\?/, "");

        if (nextSearch !== currentSearch) {
            navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
        }
    }, [filters, location.pathname, location.search, navigate]);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError("");
                const token = localStorage.getItem("cani_auth") || "";
                const res = await fetch(`${API_BASE}/pmo_executive_dashboard.php`, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : "",
                        "X-CANI-AUTH": token || "",
                    },
                });
                const txt = await res.text();
                if (!txt || !txt.trim()) throw new Error("Respuesta vacía del Dashboard PMO");
                const json = JSON.parse(txt);
                if (!res.ok || !json.success) throw new Error(json.message || "No se pudo cargar Dashboard PMO");
                setData(json);
            } catch (e) {
                setError(e.message || "Error al cargar Dashboard PMO");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const rawSummary = data?.summary || {};
    const five = data?.five_second_dashboard || {};
    const portfolio = data?.portfolio_health || [];
    const attention = data?.executive_attention || [];
    const accountability = data?.accountability_matrix || {};
    const criticalPath = data?.critical_path || [];
    const reviewFlow = data?.review_flow || {};
    const evidence = data?.evidence_traceability || {};
    const risks = data?.risk_overview || {};
    const deliverables = data?.deliverables_overview || {};
    const finance = data?.financial_overview || {};
    const pmOptions = useMemo(() => uniqueOptions(portfolio.map((p) => p.project_manager_name || "Sin PM")), [portfolio]);
    const sponsorOptions = useMemo(() => uniqueOptions(portfolio.map((p) => p.sponsor_name || "Sin sponsor")), [portfolio]);
    const activeFilterCount = [filters.search.trim(), filters.pm !== "all" ? filters.pm : "", filters.sponsor !== "all" ? filters.sponsor : "", filters.health !== "all" ? filters.health : "", filters.status !== "all" ? filters.status : ""].filter(Boolean).length;

    const filteredPortfolio = useMemo(() => {
        const q = normalizeTextForSearch(filters.search || "");
        return portfolio.filter((project) => {
            const projectText = normalizeTextForSearch(`${project.project_name || ""} ${project.project_code || ""} ${project.project_manager_name || ""} ${project.sponsor_name || ""}`);
            if (q && !projectText.includes(q)) return false;
            if (filters.pm !== "all" && (project.project_manager_name || "Sin PM") !== filters.pm) return false;
            if (filters.sponsor !== "all" && (project.sponsor_name || "Sin sponsor") !== filters.sponsor) return false;
            if (filters.health !== "all" && String(project.health_status || "").toLowerCase() !== filters.health) return false;
            if (filters.status !== "all" && String(project.status || "").toLowerCase() !== filters.status) return false;
            return true;
        });
    }, [portfolio, filters]);

    const filteredProjectIds = useMemo(() => new Set(filteredPortfolio.map((p) => Number(p.project_id))), [filteredPortfolio]);
    const filteredAttention = useMemo(() => {
        const q = normalizeTextForSearch(filters.search || "");
        return attention.filter((item) => {
            if (filteredProjectIds.size && !filteredProjectIds.has(Number(item.project_id))) return false;
            if (!q) return true;
            const text = normalizeTextForSearch(`${item.task_name || ""} ${item.wbs_code || ""} ${item.project_name || ""} ${item.project_code || ""}`);
            return text.includes(q) || filteredProjectIds.has(Number(item.project_id));
        });
    }, [attention, filteredProjectIds, filters.search]);

    const filteredCriticalPath = useMemo(() => {
        const q = normalizeTextForSearch(filters.search || "");
        return criticalPath.filter((task) => {
            if (filteredProjectIds.size && !filteredProjectIds.has(Number(task.project_id))) return false;
            if (!q) return true;
            const text = normalizeTextForSearch(`${task.task_name || ""} ${task.wbs_code || ""} ${task.project_name || ""}`);
            return text.includes(q) || filteredProjectIds.has(Number(task.project_id));
        });
    }, [criticalPath, filteredProjectIds, filters.search]);

    const filteredProjectManagers = useMemo(() => buildProjectManagersFromPortfolio(filteredPortfolio), [filteredPortfolio]);
    const summary = useMemo(() => activeFilterCount > 0 ? buildSummaryFromPortfolio(filteredPortfolio) : { ...rawSummary, health_score: rawSummary.health_score, health_tone: rawSummary.health_tone }, [activeFilterCount, filteredPortfolio, rawSummary]);

    const filteredFinance = useMemo(() => activeFilterCount > 0 ? buildFinanceFromSummary(summary, finance) : finance, [activeFilterCount, summary, finance]);
    const currentRiskLabel = filteredRiskLabel(summary, activeFilterCount);
    const currentRiskMessage = filteredRiskMessage(summary, activeFilterCount);

    const mostPressedProject = filteredPortfolio?.[0] || five?.most_pressed_project || null;
    const criticalDecision = filteredAttention?.[0] || five?.critical_decision || null;
    const accountabilityFocus = filteredProjectManagers?.[0] || five?.accountability_focus || null;

    const sponsorRows = useMemo(() => {
        const map = new Map();
        filteredPortfolio.forEach((project) => {
            const key = project.sponsor_name || "Sin sponsor";
            const current = map.get(key) || {
                name: key,
                user_id: project.sponsor_user_id || null,
                projects_count: 0,
                red_projects: 0,
                overdue_tasks: 0,
                contract_value: 0,
                currency: project.currency || "MXN",
            };
            current.projects_count += 1;
            current.red_projects += String(project.health_status || "").toLowerCase() === "rojo" ? 1 : 0;
            current.overdue_tasks += safeNumber(project.overdue_tasks);
            current.contract_value += safeNumber(project.contract_value);
            current.currency = project.currency || current.currency || "MXN";
            map.set(key, current);
        });
        return Array.from(map.values()).sort((a, b) => sponsorPressureScore(b) - sponsorPressureScore(a));
    }, [filteredPortfolio]);

    const financialCurrency = filteredPortfolio?.[0]?.currency || portfolio?.[0]?.currency || "MXN";
    const completionRate = useMemo(() => {
        return summary.total_tasks ? (safeNumber(summary.completed_tasks) / Math.max(1, safeNumber(summary.total_tasks))) * 100 : 0;
    }, [summary.completed_tasks, summary.total_tasks]);

    if (loading) {
        return (
            <MainLayout>
                <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", fontSize: 14, fontWeight: 850 }}>
                    Cargando Dashboard PMO...
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout>
                <div style={{ borderRadius: 20, background: "#FFF1F2", border: "1px solid #FECDD3", color: "#BE123C", padding: 20, fontSize: 14, fontWeight: 850 }}>
                    {error}
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div style={{ minHeight: "100vh", background: "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC" }}>
                <section style={{ marginBottom: 20, borderRadius: 28, padding: 26, background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)", border: "1px solid #E2E8F0", boxShadow: "0 20px 50px rgba(15,23,42,.07)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 11px", borderRadius: 999, background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1D4ED8", fontSize: 11, fontWeight: 950, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 14 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 999, background: "#2563EB" }} />
                            Centro Ejecutivo PMO
                        </div>
                        <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.05, color: "#0F172A", fontWeight: 950, letterSpacing: "-.05em" }}>Dashboard PMO</h1>
                        <p style={{ margin: "10px 0 0", color: "#64748B", fontSize: 14, lineHeight: 1.5, maxWidth: 850, fontWeight: 700 }}>
                            Visión de alta dirección para controlar portafolio, salud de proyectos, accountability, evidencias, riesgos, entregables y decisiones críticas.
                        </p>
                    </div>
                    <div style={{ minWidth: 270, padding: 16, borderRadius: 22, background: "#0F172A", color: "#FFFFFF", boxShadow: "0 18px 45px rgba(15,23,42,.22)" }}>
                        <div style={{ color: "#94A3B8", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Lectura inmediata</div>
                        <div style={{ marginTop: 8, fontSize: 26, fontWeight: 950 }}>{currentRiskLabel}</div>
                        <div style={{ marginTop: 6, color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>{currentRiskMessage}</div>
                    </div>
                </section>

                <ExecutiveDashboardFilters
                    filters={filters}
                    setFilters={setFilters}
                    pmOptions={pmOptions}
                    sponsorOptions={sponsorOptions}
                    activeCount={activeFilterCount}
                    onClear={() => setFilters(DEFAULT_DASHBOARD_FILTERS)}
                />

                <ActiveFilterSummary
                    filters={filters}
                    activeCount={activeFilterCount}
                    visibleProjects={filteredPortfolio.length}
                    summary={summary}
                    onClear={() => setFilters(DEFAULT_DASHBOARD_FILTERS)}
                />

                <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 20 }}>
                    <FiveSecondCard
                        label="¿Estamos en riesgo?"
                        title={currentRiskLabel}
                        subtitle={`Score de salud: ${formatNumber(summary.health_score, 0)} / 100. ${summary.red_projects || 0} proyecto(s) en rojo.`}
                        tone={toneFromExecutive(summary.health_tone)}
                        icon="◉"
                        onClick={() => goProjects({ health_status: "rojo" })}
                    />
                    <FiveSecondCard
                        label="¿Dónde mirar primero?"
                        title={mostPressedProject?.project_name || "Sin proyecto presionado"}
                        subtitle={mostPressedProject ? `${mostPressedProject.project_code || "PRJ"} · ${formatNumber(mostPressedProject.open_tasks)} abiertas · ${formatNumber(mostPressedProject.overdue_tasks)} vencidas` : "No se detectó presión dominante."}
                        tone={toneFromExecutive(mostPressedProject?.executive_tone || mostPressedProject?.health_status)}
                        icon="▲"
                        onClick={() => goProjectDetail(mostPressedProject?.project_id)}
                    />
                    <FiveSecondCard
                        label="¿Qué decisión requiere atención?"
                        title={criticalDecision ? `${criticalDecision.wbs_code || ""} ${criticalDecision.task_name || "Tarea"}` : "Sin decisión crítica"}
                        subtitle={criticalDecision ? `${criticalDecision.project_name} · ${criticalDecision.recommended_action}` : "No hay acciones críticas en el Centro de Decisiones."}
                        tone={toneFromSeverity(criticalDecision?.severity)}
                        icon="!"
                        onClick={() => criticalDecision ? goTaskDetail(criticalDecision.task_id, criticalDecision.project_id) : goDecisionCenter()}
                    />
                    <FiveSecondCard
                        label="¿Quién concentra presión?"
                        title={accountabilityFocus?.name || "Sin foco de accountability"}
                        subtitle={accountabilityFocus ? `${formatNumber(accountabilityFocus.projects_count)} proyecto(s) · ${formatNumber(accountabilityFocus.red_projects)} en rojo · ${formatNumber(accountabilityFocus.overdue_tasks)} vencidas` : "Sin responsable dominante de presión."}
                        tone={safeNumber(accountabilityFocus?.overdue_tasks) > 0 || safeNumber(accountabilityFocus?.red_projects) > 0 ? "warning" : "success"}
                        icon="◎"
                    />
                </section>

                <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16, marginBottom: 20 }}>
                    <KpiCard title="Proyectos activos" value={summary.active_projects || 0} subtitle={`Total: ${formatNumber(summary.total_projects)}`} tone="info" icon="▦" onClick={() => goProjects({ status: "activo" })} />
                    <KpiCard title="Proyectos en rojo" value={summary.red_projects || 0} subtitle={`${formatNumber(summary.yellow_projects)} amarillos · ${formatNumber(summary.green_projects)} verdes`} tone={safeNumber(summary.red_projects) > 0 ? "danger" : "success"} icon="▲" onClick={() => goProjects({ health_status: "rojo" })} />
                    <KpiCard title="Tareas abiertas" value={summary.open_tasks || 0} subtitle={`${formatNumber(summary.completed_tasks)} cerradas de ${formatNumber(summary.total_tasks)}`} tone="neutral" icon="◫" onClick={() => goMyWork("activas")} />
                    <KpiCard title="Tareas vencidas" value={summary.overdue_tasks || 0} subtitle={`${formatNumber(summary.blocked_tasks)} bloqueadas`} tone={safeNumber(summary.overdue_tasks) > 0 ? "danger" : "success"} icon="!" onClick={() => goMyWork("vencidas")} />
                    <KpiCard title="En revisión" value={summary.review_tasks || 0} subtitle={`${formatNumber(summary.correction_tasks)} con corrección`} tone={safeNumber(summary.review_tasks) > 0 ? "purple" : "success"} icon="◐" onClick={() => goMyWork("revision")} />
                    <KpiCard title="Cierre operativo" value={`${formatNumber(completionRate, 0)}%`} subtitle={`${formatNumber(summary.completed_tasks)} de ${formatNumber(summary.total_tasks)} tareas`} tone="success" icon="✓" />
                </section>

                <section style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(340px, .75fr)", gap: 20, marginBottom: 20 }}>
                    <HealthScoreMeter score={summary.health_score} tone={summary.health_tone} summary={summary} />
                    <Block title="Atención directiva" subtitle="Acciones críticas o de seguimiento que el sistema recomienda atender primero." action={<button type="button" style={secondaryButton} onClick={goDecisionCenter}>Abrir Centro de Decisiones</button>}>
                        <div style={{ display: "grid", gap: 12 }}>
                            {filteredAttention.slice(0, 4).map((item) => <ExecutiveAttentionCard key={item.task_id} item={item} onOpenTask={goTaskDetail} />)}
                            {!filteredAttention.length ? <EmptyState text="No hay acciones directivas pendientes en esta vista. La selección actual no muestra señales críticas de intervención." /> : null}
                        </div>
                    </Block>
                </section>

                <section style={{ marginBottom: 20 }}>
                    <PressureAnalysisBlock
                        projects={filteredPortfolio}
                        projectManagers={filteredProjectManagers}
                        onOpenProject={goProjectDetail}
                        onOpenPM={(row) => goProjects({ logged_user_id: row.user_id, view_mode: "my_projects" })}
                    />
                </section>

                <section style={{ marginBottom: 20 }}>
                    <ProjectHealthSection rows={filteredPortfolio} onOpenProject={goProjectDetail} onOpenProjects={() => goProjects({ status: "activo" })} />
                </section>

                <section style={{ marginBottom: 20 }}>
                    <RoleAccountabilityMatrix
                        projectManagers={filteredProjectManagers}
                        sponsors={sponsorRows}
                        responsibles={accountability.responsibles || []}
                        reviewers={accountability.reviewers || []}
                        onOpenPM={(row) => goProjects({ logged_user_id: row.user_id, view_mode: "my_projects" })}
                        onOpenSponsor={(row) => row.user_id ? goProjects({ logged_user_id: row.user_id, view_mode: "as_sponsor" }) : goProjects({ search: row.name })}
                    />
                </section>

                <section style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 20, marginBottom: 20 }}>
                    <Block title="Ruta crítica operativa" subtitle="Tareas con vencimiento, dependencia o potencial impacto en continuidad. Da clic en un renglón para abrir la ficha PRO de la tarea.">
                        <ExecutiveGrid
                            rows={filteredCriticalPath.slice(0, 10)}
                            minWidth={980}
                            getKey={(task) => task.task_id}
                            onRowClick={(task) => goTaskDetail(task.task_id, task.project_id)}
                            emptyText="No hay ruta crítica activa para esta vista. No se detectan vencimientos, bloqueos o dependencias con presión relevante."
                            defaultSortKey="criticality_score"
                            defaultSortDirection="desc"
                            columns={[
                                {
                                    key: "task",
                                    label: "Tarea",
                                    title: "Tarea priorizada por vencimiento, dependencia o impacto operativo.",
                                    render: (task) => (
                                        <div>
                                            <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>{task.wbs_code ? `${task.wbs_code} · ` : ""}{cleanText(task.task_name)}</div>
                                            <div style={{ marginTop: 4, color: "#64748B", fontSize: 11, fontWeight: 750 }}>{task.project_name}</div>
                                        </div>
                                    ),
                                },
                                { key: "owner_name", label: "Responsable", title: "Responsable asignado para ejecutar y documentar esta tarea crítica.", nowrap: true, render: (task) => task.owner_name || "Sin responsable" },
                                { key: "dependency_type", label: "Dependencia", title: "Tipo de dependencia de la tarea: FS, SS, FF o SF.", nowrap: true, render: (task) => task.dependency_type || "FS" },
                                { key: "status", label: "Estatus", title: "Estado operativo actual de la tarea.", nowrap: true, render: (task) => <StatusPill tone={task.status === "bloqueada" ? "danger" : task.status === "en_proceso" ? "info" : "neutral"}>{label(task.status)}</StatusPill> },
                                { key: "due_date", label: "Fecha", title: "Fecha compromiso de la tarea para seguimiento PMO.", nowrap: true, render: (task) => formatDate(task.due_date) },
                                { key: "criticality_score", label: "Señal", title: "Señal ejecutiva de ruta crítica: muestra vencimiento o nivel de impacto por dependencia, prioridad y continuidad.", nowrap: true, render: (task) => <StatusPill tone={safeNumber(task.days_overdue) > 0 ? "danger" : pressureTone(task.criticality_score)}>{safeNumber(task.days_overdue) > 0 ? `Vencida ${task.days_overdue} día(s)` : `Señal baja · ${formatNumber(task.criticality_score)} pts`}</StatusPill> },
                            ]}
                        />
                    </Block>
                </section>

                <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 20 }}>
                    <Block title="Revisión y dictámenes" subtitle="Flujo responsable → revisor y presión de aprobación.">
                        <CompactMetricGrid items={[
                            { label: "Pendientes", value: formatNumber(reviewFlow.pending_reviews), tone: safeNumber(reviewFlow.pending_reviews) > 0 ? "purple" : "success", help: "Esperando decisión" },
                            { label: "Aprobadas", value: formatNumber(reviewFlow.approved_reviews), tone: "success", help: "Dictámenes aprobados" },
                            { label: "Con cambios", value: formatNumber(reviewFlow.change_reviews), tone: safeNumber(reviewFlow.change_reviews) > 0 ? "warning" : "neutral", help: "Solicitudes de ajuste" },
                            { label: "Rechazadas", value: formatNumber(reviewFlow.rejected_reviews), tone: safeNumber(reviewFlow.rejected_reviews) > 0 ? "danger" : "neutral", help: "Dictámenes negativos" },
                        ]} />
                    </Block>

                    <Block title="Evidencia y geocerca" subtitle="Trazabilidad documental y validación territorial.">
                        <CompactMetricGrid items={[
                            { label: "Evidencias", value: formatNumber(evidence.total_evidence), tone: "info", help: "Total cargadas" },
                            { label: "Con geo", value: formatNumber(evidence.geo_evidence), tone: "success", help: "Ubicación capturada" },
                            { label: "Fuera de rango", value: formatNumber(evidence.out_of_range), tone: safeNumber(evidence.out_of_range) > 0 ? "warning" : "success", help: "Validación territorial" },
                            { label: "Excepciones", value: formatNumber(evidence.exceptions_approved), tone: "purple", help: "Autorizadas" },
                            { label: "Sin evidencia", value: formatNumber(evidence.tasks_missing_evidence), tone: safeNumber(evidence.tasks_missing_evidence) > 0 ? "warning" : "success", help: "Tareas pendientes" },
                            { label: "Sin geo", value: formatNumber(evidence.tasks_missing_geo), tone: safeNumber(evidence.tasks_missing_geo) > 0 ? "warning" : "success", help: "Geoevidencia pendiente" },
                        ]} />
                    </Block>

                    <Block title="Riesgos y entregables" subtitle="Exposición operativa y compromisos formales.">
                        <CompactMetricGrid items={[
                            { label: "Riesgos abiertos", value: formatNumber(risks.total_open_risks), tone: safeNumber(risks.total_open_risks) > 0 ? "warning" : "success", help: "En seguimiento" },
                            { label: "Riesgos altos", value: formatNumber(risks.high_open_risks), tone: safeNumber(risks.high_open_risks) > 0 ? "danger" : "success", help: "Impacto alto" },
                            { label: "Entregables", value: formatNumber(deliverables.total_deliverables), tone: "neutral", help: "Registrados" },
                            { label: "Vencidos", value: formatNumber(deliverables.overdue_deliverables), tone: safeNumber(deliverables.overdue_deliverables) > 0 ? "danger" : "success", help: "Compromisos fuera de fecha" },
                        ]} />
                    </Block>
                </section>

                <section style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 20, marginBottom: 20 }}>
                    <Block title="Finanzas ejecutivas" subtitle="Lectura comercial de presupuesto, contrato y ejecución financiera.">
                        <CompactMetricGrid items={[
                            { label: "Presupuesto", value: formatMoney(filteredFinance.budget_planned, financialCurrency), tone: "info", help: activeFilterCount > 0 ? "Planeado en vista filtrada" : "Planeado" },
                            { label: "Ejercido", value: formatMoney(filteredFinance.budget_actual, financialCurrency), tone: "warning", help: activeFilterCount > 0 ? "Actual en vista filtrada" : "Actual" },
                            { label: "Contrato", value: formatMoney(filteredFinance.contract_value, financialCurrency), tone: "purple", help: activeFilterCount > 0 ? "Valor filtrado" : "Valor contractual" },
                            { label: "Pendiente pago", value: formatMoney(filteredFinance.pending_expenses, financialCurrency), tone: "neutral", help: "Gastos pendientes" },
                        ]} />
                        <div title="Lectura ejecutiva automática a partir de presupuesto planeado, presupuesto ejercido y gastos pendientes." style={{ marginTop: 14, padding: 14, borderRadius: 18, background: "#F8FAFC", border: "1px solid #E2E8F0", color: "#334155", fontSize: 12, fontWeight: 850, lineHeight: 1.45 }}>
                            {financeExecutionText(filteredFinance)}
                        </div>
                    </Block>
                    <Block title="Mapa de evidencias" subtitle="Vista preparada para georreferenciar proyectos, evidencias y puntos de ejecución.">
                        <div style={{ borderRadius: 22, minHeight: 230, background: "linear-gradient(135deg, rgba(15,23,42,.96), rgba(30,41,59,.96)), radial-gradient(circle at 20% 20%, rgba(37,99,235,.5), transparent 28%)", border: "1px solid rgba(148,163,184,.20)", color: "#FFFFFF", padding: 20, position: "relative", overflow: "hidden", boxShadow: "0 24px 55px rgba(15,23,42,.22)" }}>
                            <div style={{ display: "inline-flex", padding: "6px 10px", borderRadius: 999, background: "rgba(96,165,250,.18)", border: "1px solid rgba(96,165,250,.35)", color: "#BFDBFE", fontSize: 11, fontWeight: 950, letterSpacing: ".06em", textTransform: "uppercase" }}>Próximamente</div>
                            <h2 style={{ margin: "14px 0 0", fontSize: 24, fontWeight: 950, letterSpacing: "-.04em" }}>Mapa operativo</h2>
                            <p style={{ margin: "8px 0 0", color: "#CBD5E1", fontSize: 13, lineHeight: 1.55, maxWidth: 470, fontWeight: 650 }}>Visualización geográfica de proyectos, fotos, responsables, fechas, evidencias fuera de rango y excepciones autorizadas.</p>
                            <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>{["GPS", "Fotos", "Excepciones", "Proyecto", "Responsable"].map((tag) => <span key={tag} style={{ padding: "7px 10px", borderRadius: 999, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.13)", color: "#E2E8F0", fontSize: 11, fontWeight: 900 }}>{tag}</span>)}</div>
                        </div>
                    </Block>
                </section>
            </div>
        </MainLayout>
    );
}
